import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import dayjs from 'dayjs';
import { renderToString } from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import './HeatMapChartCart.scss';
import { ColorRing } from 'react-loader-spinner';

import CustomTooltip from './CustomTooltip';
import { heatMapChartOptions } from './chartOptions';
import { drawWeekSeparators, simulateCalendar } from './HeatmapFunctions';

import CustomMonthTooltip from 'pages/ObjectAnalitics/MCTCharts/6heatmapChart/CustomMonthTooltip';
import CustomYearTooltip from 'pages/ObjectAnalitics/MCTCharts/6heatmapChart/CustomYearTooltip';
import useGetTrapDetailHeatMapChartData from 'hooks/Traps/Trap/useGetTrapDetailHeatMapChartData';
import useGetTrapDetailHeatMapSRBMChartData from 'hooks/Traps/Trap/useGetTrapDetailHeatMapSRBMChartData';
import { HeatMapChartData, Ranges, Series } from 'api/traps/types';
import { TrapType } from 'components/AnalyticsPage/TrapType';

interface ChartCartProps {
  slug: string;
  type: TrapType;
  currentYear: string;
  selectedMonth: string | null;
  setSelectedMonth: (month: string) => void;
  chartHeaight: number;
  setChartHeight: (height: number) => void;
  isCompare?: boolean;
}

const HeatMapChartCart: React.FC<ChartCartProps> = ({
  slug,
  type,
  currentYear,
  selectedMonth,
  setSelectedMonth,
  chartHeaight,
  setChartHeight,
  isCompare,
}) => {
  const { t } = useTranslation();
  let start_datetime: string | undefined;
  let end_datetime: string | undefined;

  const [isChartReady, setIsChartReady] = useState(false);
  if (selectedMonth !== null) {
    const monthStr = String(selectedMonth).padStart(2, '0');
    start_datetime = `${currentYear}-${monthStr}-01T00:00:00`;
    end_datetime = dayjs(start_datetime).endOf('month').format('YYYY-MM-DDT23:59:59');
  } else {
    // Для годового отчета (без месяца)
    start_datetime = `${currentYear}-01-01T00:00:00`;
    end_datetime = `${currentYear}-12-31T23:59:59`;
  }

  const isSRBM = type === TrapType.SRBM;
  const srbmQuery = useGetTrapDetailHeatMapSRBMChartData(slug, start_datetime!, end_datetime!, isCompare, isSRBM);
  const mctQuery = useGetTrapDetailHeatMapChartData(slug, type, start_datetime!, end_datetime!, isCompare, isSRBM);
  const { data, refetch, isLoading, isFetching } = isSRBM ? srbmQuery : mctQuery;
  const [seriesList, setSeriesList] = useState<Series[]>([]);
  const [ranges] = useState<Ranges[]>([
    { from: -1000000, to: -1000000, name: 'upcoming', color: '#9563FF' },

    { from: 0, to: 0, name: '0', color: '#8BC34A' },

    { from: 1, to: 1, name: '1', color: '#FDEfE0', caught: true },
    { from: 2, to: 2, name: '2', color: '#F9B4B4', caught: true },
    { from: 3, to: 5, name: '3-5', color: '#F27A7A', caught: true },
    { from: 6, to: 10, name: '6–10', color: '#E94B4B', caught: true },
    { from: 11, to: 20, name: '11–20', color: '#C62828', caught: true },
    { from: 21, to: 100000, name: '20+', color: '#7F1D1D', caught: true },

    { from: -1, to: -1, name: '1', color: '#FFE6C9', caught: false },
    { from: -2, to: -2, name: '2', color: '#FFC98B', caught: false },
    { from: -4, to: -3, name: '3-4', color: '#F4A24F', caught: false },
    { from: -100000, to: -5, name: '5+', color: '#E07A1F', caught: false },
  ]);

  useEffect(() => {
    setIsChartReady(false); // Mark chart as not ready immediately when params change
    refetch();
  }, [slug, refetch, start_datetime, end_datetime, currentYear]);

  useEffect(() => {
    if (!isLoading && !isFetching && data !== undefined) {
      const simulated = simulateCalendar(parseInt(currentYear, 10), selectedMonth, data ?? []);
      setSeriesList(simulated);
      setIsChartReady(true); // Data processed, chart is ready to show
    }
  }, [currentYear, selectedMonth, data, isLoading, isFetching]);

  const chartData: HeatMapChartData = {
    seriesList,
    ranges,
  };

  const baseChartOptions = heatMapChartOptions(
    chartData,
    type,
    setSelectedMonth,
    selectedMonth,
    currentYear,
    setChartHeight,
    t,
    true,
    props => {
      const row = seriesList?.[props.seriesIndex];
      const point = row?.data?.[props.dataPointIndex];

      if (row?.name === '__separator__' || point?.isSeparator) {
        return '<div></div>';
      }

      if (isSRBM) {
        const TooltipComponent = selectedMonth ? CustomMonthTooltip : CustomYearTooltip;
        return renderToString(
          <TooltipComponent {...props} currentYear={currentYear} selectedMonth={selectedMonth} isSRBM />,
        );
      }

      return renderToString(<CustomTooltip {...props} currentYear={currentYear} selectedMonth={selectedMonth} />);
    },
  );

  const chartOptions = {
    ...baseChartOptions,
    chart: {
      ...baseChartOptions.chart,
      events: {
        ...baseChartOptions.chart?.events,

        mounted: function (chartContext: any) {
          drawWeekSeparators(chartContext, selectedMonth, currentYear);
        },
        updated: function (chartContext: any) {
          drawWeekSeparators(chartContext, selectedMonth, currentYear);
        },

        dataPointMouseEnter: function (_event: any, chartContext: any, config: any) {
          if (!config || !config.w) return;

          const seriesName = String(config.w.config.series[config.seriesIndex].name).trim();
          const labels = chartContext.el.querySelectorAll('.apexcharts-yaxis-label');

          labels.forEach((label: any) => {
            const tspan = label.querySelector('tspan');
            const cleanText = (tspan ? tspan.textContent : label.textContent)?.trim();

            if (!cleanText) return;

            const isSeriesNumeric = /^\d+$/.test(seriesName);

            let isMatch = false;

            if (isSeriesNumeric) {
              const labelDayNumber = cleanText.match(/\d+$/)?.[0];
              isMatch = labelDayNumber === seriesName;
            } else {
              isMatch = cleanText === seriesName;
            }

            if (isMatch) {
              label.classList.add('active-month-label');
              if (tspan) {
                tspan.style.fill = '#9563FF';
              } else {
                label.style.fill = '#9563FF';
              }
            }
          });
        },

        dataPointMouseLeave: function (_event: any, chartContext: any, _config: any) {
          if (!chartContext || !chartContext.el) return;

          const labels = chartContext.el.querySelectorAll('.apexcharts-yaxis-label');
          labels.forEach((label: any) => {
            label.classList.remove('active-month-label');
            label.style.fill = '';

            const tspan = label.querySelector('tspan');
            if (tspan) {
              tspan.style.fill = '';
            }
          });
        },
      },
    },
  };
  const chartOptionsWithNoAnimation = {
    ...chartOptions,
    chart: {
      ...chartOptions.chart,
      animations: {
        enabled: false, // DISABLE animations
      },
    },
  };
  const showLoader = isLoading || isFetching || !isChartReady;
  return (
    <div style={{ overflowX: 'auto', scrollPaddingRight: '16px' }}>
      <div className="card d-flex m-0" style={{ minWidth: '768px' }}>
        <div className="card-body">
          {showLoader ? (
            <div className="d-flex justify-content-center align-items-center" style={{ height: chartHeaight }}>
              <ColorRing
                visible
                height="54"
                width="54"
                ariaLabel="color-ring-loading"
                wrapperStyle={{}}
                wrapperClass="color-ring-wrapper"
                colors={['#9C7AE2', '#E27A8C', '#C0E27A', '#7AE2D0', '#F7F18F']}
              />
            </div>
          ) : (
            <ReactApexChart
              key={`${currentYear}-${selectedMonth}-${slug}`}
              options={chartOptionsWithNoAnimation}
              series={seriesList}
              type="heatmap"
              height={chartHeaight}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default HeatMapChartCart;
